import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { Dialog } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputNumber } from 'primeng/inputnumber';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { MultiSelect } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { Textarea } from 'primeng/textarea';
import { Toast } from 'primeng/toast';
import { Tooltip } from 'primeng/tooltip';
import {
    FakeReviewService,
    UpdateCommentPatch,
} from '@/app/services/fake-review.service';
import {
    FakeReviewComment,
    FakeReviewCommentAddDto,
    RatingCounts,
} from '@/lib-backend';

interface CommentRow {
    id: number;
    Text: string;
    /** Comma-separated tags; split on save. */
    TagsRaw: string;
    MinRating: number;
    MaxRating: number;
}

interface EditForm {
    Text: string;
    TagsRaw: string;
    MinRating: number;
    MaxRating: number;
}

const RATING_MIN = 1;
const RATING_MAX = 5;

const IMPORT_EXAMPLE = `[
  { "Text": "Great game!", "Tags": ["fun", "addictive"], "MinRating": 4, "MaxRating": 5 },
  { "Text": "Bad luck...", "Tags": ["slow"],            "MinRating": 1, "MaxRating": 2 }
]`;

const MANAGE_FETCH_CHUNK = 500;

type TabKey = 'add' | 'manage';

@Component({
    selector: 'app-fake-comments',
    standalone: true,
    imports: [
        FormsModule,
        Button,
        Checkbox,
        Dialog,
        IconField,
        InputIcon,
        InputNumber,
        InputText,
        Message,
        MultiSelect,
        TableModule,
        Tab,
        TabList,
        TabPanel,
        TabPanels,
        Tabs,
        Textarea,
        Toast,
        Tooltip,
    ],
    providers: [MessageService],
    templateUrl: './fake-comments.html',
    styleUrl: './fake-comments.scss',
})
export class FakeComments {
    private readonly fakeReviewService = inject(FakeReviewService);
    private readonly messageService = inject(MessageService);

    private nextRowId = 1;

    // ── Tabs ────────────────────────────────────────────────────────────────
    protected readonly activeTab = signal<TabKey>('add');

    // ── Add tab ─────────────────────────────────────────────────────────────
    protected readonly rows = signal<CommentRow[]>([this.makeRow()]);
    protected readonly saving = signal(false);
    protected readonly error = signal<string | null>(null);

    protected readonly pageSize = signal(20);
    protected readonly firstIndex = signal(0);
    protected readonly pageSizeOptions = [10, 20, 50, 100];

    protected readonly ratingMin = RATING_MIN;
    protected readonly ratingMax = RATING_MAX;

    protected readonly validCount = computed(
        () => this.rows().filter((r) => this.isRowValid(r) === null).length,
    );

    // ── Confirmation dialogs (Add tab) ──────────────────────────────────────
    protected readonly clearConfirmVisible = signal(false);
    protected readonly removeConfirmVisible = signal(false);
    protected readonly removeConfirmRow = signal<CommentRow | null>(null);
    protected readonly removeConfirmDontAsk = signal(false);
    /** Session-wide: once user opts out, remove-row prompts are skipped. */
    private skipRemoveConfirm = false;

    // ── Import dialog (Add tab) ─────────────────────────────────────────────
    protected readonly importDialogVisible = signal(false);
    protected readonly importJsonText = signal('');
    protected readonly importError = signal<string | null>(null);
    protected readonly importDragOver = signal(false);
    protected readonly importExample = IMPORT_EXAMPLE;

    // ── Manage tab ──────────────────────────────────────────────────────────
    protected readonly allComments = signal<FakeReviewComment[]>([]);
    protected readonly manageLoading = signal(false);
    protected readonly manageError = signal<string | null>(null);
    protected readonly manageSearch = signal('');
    protected readonly selectedTags = signal<string[]>([]);
    private manageInitialized = false;

    protected readonly availableTags = computed(() => {
        const set = new Set<string>();
        for (const c of this.allComments()) {
            for (const t of c.Tags) set.add(t);
        }
        return Array.from(set).sort();
    });

    protected readonly filteredComments = computed(() => {
        const list = this.allComments();
        const search = this.manageSearch().trim().toLowerCase();
        const tags = this.selectedTags();
        return list.filter((c) => {
            if (search && !c.Text.toLowerCase().includes(search)) return false;
            if (tags.length > 0 && !tags.some((t) => c.Tags.includes(t))) {
                return false;
            }
            return true;
        });
    });

    protected readonly commentsStat = computed<{
        TotalCount: number;
        PerRating: RatingCounts;
    }>(() => {
        const filtered = this.filteredComments();
        const perRating: RatingCounts = {
            Rating1: 0,
            Rating2: 0,
            Rating3: 0,
            Rating4: 0,
            Rating5: 0,
        };
        for (const c of filtered) {
            for (let r = c.MinRating; r <= c.MaxRating; r++) {
                if (r === 1) perRating.Rating1++;
                else if (r === 2) perRating.Rating2++;
                else if (r === 3) perRating.Rating3++;
                else if (r === 4) perRating.Rating4++;
                else if (r === 5) perRating.Rating5++;
            }
        }
        return { TotalCount: filtered.length, PerRating: perRating };
    });

    // ── Edit dialog (Manage tab) ────────────────────────────────────────────
    protected readonly editDialogVisible = signal(false);
    protected readonly editComment = signal<FakeReviewComment | null>(null);
    protected readonly editForm = signal<EditForm | null>(null);
    protected readonly editSaving = signal(false);
    protected readonly editError = signal<string | null>(null);

    // ── Delete dialog (Manage tab) ──────────────────────────────────────────
    protected readonly deleteConfirmVisible = signal(false);
    protected readonly deleteConfirmComment = signal<FakeReviewComment | null>(
        null,
    );
    protected readonly deleting = signal(false);

    // ────────────────────────────────────────────────────────────────────────
    // Add tab
    // ────────────────────────────────────────────────────────────────────────

    protected addRow(): void {
        this.rows.update((list) => [...list, this.makeRow()]);
        this.jumpToLastPage();
    }

    protected requestRemoveRow(row: CommentRow): void {
        if (this.isRowBlank(row) || this.skipRemoveConfirm) {
            this.removeRow(row);
            return;
        }
        this.removeConfirmRow.set(row);
        this.removeConfirmDontAsk.set(false);
        this.removeConfirmVisible.set(true);
    }

    protected confirmRemoveRow(): void {
        const row = this.removeConfirmRow();
        if (this.removeConfirmDontAsk()) this.skipRemoveConfirm = true;
        this.removeConfirmVisible.set(false);
        this.removeConfirmRow.set(null);
        if (row) this.removeRow(row);
    }

    protected cancelRemoveRow(): void {
        this.removeConfirmVisible.set(false);
        this.removeConfirmRow.set(null);
    }

    private removeRow(row: CommentRow): void {
        this.rows.update((list) => list.filter((r) => r.id !== row.id));
        if (this.rows().length === 0) this.addRow();
        this.clampFirstIndex();
    }

    protected onPage(event: { first: number; rows: number }): void {
        this.firstIndex.set(event.first);
        this.pageSize.set(event.rows);
    }

    /** Set first index to the start of the page that contains the last row. */
    private jumpToLastPage(): void {
        const total = this.rows().length;
        const size = this.pageSize();
        this.firstIndex.set(Math.max(0, Math.floor((total - 1) / size) * size));
    }

    /** Keep firstIndex within bounds after removal. */
    private clampFirstIndex(): void {
        const total = this.rows().length;
        const size = this.pageSize();
        const maxFirst = Math.max(0, Math.floor((total - 1) / size) * size);
        if (this.firstIndex() > maxFirst) this.firstIndex.set(maxFirst);
    }

    protected async save(): Promise<void> {
        this.error.set(null);

        const rows = this.rows();
        const payload: FakeReviewCommentAddDto[] = [];

        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (this.isRowBlank(r)) continue;

            const err = this.isRowValid(r);
            if (err !== null) {
                this.error.set(`Row ${i + 1}: ${err}`);
                return;
            }
            payload.push({
                Text: r.Text.trim(),
                Tags: this.parseTags(r.TagsRaw),
                MinRating: r.MinRating,
                MaxRating: r.MaxRating,
            });
        }

        if (payload.length === 0) {
            this.error.set('Fill at least one row.');
            return;
        }

        this.saving.set(true);
        try {
            const outcome = await this.fakeReviewService.addComments(payload);
            if (outcome.result === 'Ok') {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Comments saved',
                    detail: `${outcome.insertedCount} of ${payload.length} inserted.`,
                });
                this.reset();
                if (this.manageInitialized) this.reloadComments();
            } else {
                this.error.set('Failed to save comments.');
            }
        } finally {
            this.saving.set(false);
        }
    }

    protected requestClear(): void {
        const allBlank = this.rows().every((r) => this.isRowBlank(r));
        if (allBlank) {
            this.reset();
            return;
        }
        this.clearConfirmVisible.set(true);
    }

    protected confirmClear(): void {
        this.clearConfirmVisible.set(false);
        this.reset();
    }

    protected cancelClear(): void {
        this.clearConfirmVisible.set(false);
    }

    private reset(): void {
        this.rows.set([this.makeRow()]);
        this.error.set(null);
        this.firstIndex.set(0);
    }

    private makeRow(): CommentRow {
        return {
            id: this.nextRowId++,
            Text: '',
            TagsRaw: '',
            MinRating: RATING_MIN,
            MaxRating: RATING_MAX,
        };
    }

    /** Returns a validation message if invalid, or null if the row passes. Blank rows are skipped (caller checks {@link isRowBlank}). */
    private isRowValid(r: CommentRow): string | null {
        if (r.Text.trim().length === 0) return 'Text is required.';
        if (this.parseTags(r.TagsRaw).length === 0)
            return 'At least one tag is required.';
        if (!Number.isFinite(r.MinRating) || !Number.isFinite(r.MaxRating))
            return 'Rating must be a number.';
        if (r.MinRating < RATING_MIN || r.MinRating > RATING_MAX)
            return `MinRating must be in [${RATING_MIN}..${RATING_MAX}].`;
        if (r.MaxRating < RATING_MIN || r.MaxRating > RATING_MAX)
            return `MaxRating must be in [${RATING_MIN}..${RATING_MAX}].`;
        if (r.MinRating > r.MaxRating) return 'MinRating must be ≤ MaxRating.';
        return null;
    }

    private isRowBlank(r: CommentRow): boolean {
        return (
            r.Text.trim().length === 0 && this.parseTags(r.TagsRaw).length === 0
        );
    }

    private parseTags(raw: string): string[] {
        return raw
            .split(',')
            .map((t) => t.trim())
            .filter((t) => t.length > 0);
    }

    // ── Import dialog ───────────────────────────────────────────────────────

    protected openImport(): void {
        this.importJsonText.set('');
        this.importError.set(null);
        this.importDragOver.set(false);
        this.importDialogVisible.set(true);
    }

    protected closeImport(): void {
        this.importDialogVisible.set(false);
    }

    protected onImportFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        if (file) this.readFile(file);
    }

    protected onImportDragOver(event: DragEvent): void {
        event.preventDefault();
        this.importDragOver.set(true);
    }

    protected onImportDragLeave(): void {
        this.importDragOver.set(false);
    }

    protected onImportDrop(event: DragEvent): void {
        event.preventDefault();
        this.importDragOver.set(false);
        const file = event.dataTransfer?.files?.[0];
        if (file) this.readFile(file);
    }

    private readFile(file: File): void {
        this.importError.set(null);
        const reader = new FileReader();
        reader.onload = () => {
            this.importJsonText.set(
                typeof reader.result === 'string' ? reader.result : '',
            );
        };
        reader.onerror = () => {
            this.importError.set('Failed to read file.');
        };
        reader.readAsText(file);
    }

    protected confirmImport(): void {
        const raw = this.importJsonText().trim();
        if (!raw) {
            this.importError.set('Paste JSON or drop a file.');
            return;
        }

        let parsed: unknown;
        try {
            parsed = JSON.parse(raw);
        } catch (e) {
            this.importError.set(`Invalid JSON: ${(e as Error).message}`);
            return;
        }

        if (!Array.isArray(parsed)) {
            this.importError.set('Top-level value must be a JSON array.');
            return;
        }

        const result = this.parseImport(parsed);
        if (result.error) {
            this.importError.set(result.error);
            return;
        }

        this.rows.update((list) => {
            const trimmed = list.filter((r) => !this.isRowBlank(r));
            return [...trimmed, ...result.rows];
        });
        this.jumpToLastPage();
        this.messageService.add({
            severity: 'success',
            summary: 'Comments imported',
            detail: `${result.rows.length} row(s) added — review and click Save.`,
        });
        this.importDialogVisible.set(false);
    }

    private parseImport(arr: unknown[]): {
        rows: CommentRow[];
        error: string | null;
    } {
        const rows: CommentRow[] = [];
        for (let i = 0; i < arr.length; i++) {
            const item = arr[i];
            if (item === null || typeof item !== 'object') {
                return { rows: [], error: `Item ${i}: must be an object.` };
            }
            const obj = item as Record<string, unknown>;

            const text = obj['Text'];
            if (typeof text !== 'string' || text.trim().length === 0) {
                return {
                    rows: [],
                    error: `Item ${i}: "Text" is required and must be a non-empty string.`,
                };
            }

            const tagsRaw = obj['Tags'];
            if (!Array.isArray(tagsRaw) || tagsRaw.length === 0) {
                return {
                    rows: [],
                    error: `Item ${i}: "Tags" is required and must be a non-empty array.`,
                };
            }
            const tags: string[] = [];
            for (let j = 0; j < tagsRaw.length; j++) {
                const t = tagsRaw[j];
                if (typeof t !== 'string' || t.trim().length === 0) {
                    return {
                        rows: [],
                        error: `Item ${i}: "Tags[${j}]" must be a non-empty string.`,
                    };
                }
                tags.push(t.trim());
            }

            const min = obj['MinRating'];
            const max = obj['MaxRating'];
            if (
                typeof min !== 'number' ||
                !Number.isInteger(min) ||
                min < RATING_MIN ||
                min > RATING_MAX
            ) {
                return {
                    rows: [],
                    error: `Item ${i}: "MinRating" must be an integer in [${RATING_MIN}..${RATING_MAX}].`,
                };
            }
            if (
                typeof max !== 'number' ||
                !Number.isInteger(max) ||
                max < RATING_MIN ||
                max > RATING_MAX
            ) {
                return {
                    rows: [],
                    error: `Item ${i}: "MaxRating" must be an integer in [${RATING_MIN}..${RATING_MAX}].`,
                };
            }
            if (min > max) {
                return {
                    rows: [],
                    error: `Item ${i}: "MinRating" must be ≤ "MaxRating".`,
                };
            }

            rows.push({
                id: this.nextRowId++,
                Text: text.trim(),
                TagsRaw: tags.join(', '),
                MinRating: min,
                MaxRating: max,
            });
        }
        return { rows, error: null };
    }

    protected trackByRowId(_index: number, item: CommentRow): number {
        return item.id;
    }

    // ────────────────────────────────────────────────────────────────────────
    // Manage tab
    // ────────────────────────────────────────────────────────────────────────

    protected onTabChange(value: string | number | undefined): void {
        const key = value === 'manage' ? 'manage' : 'add';
        this.activeTab.set(key);
        if (key === 'manage' && !this.manageInitialized) {
            this.manageInitialized = true;
            this.reloadComments();
        }
    }

    protected clearFilters(): void {
        this.manageSearch.set('');
        this.selectedTags.set([]);
    }

    protected async reloadComments(): Promise<void> {
        if (this.manageLoading()) return;
        this.manageLoading.set(true);
        this.manageError.set(null);
        const collected: FakeReviewComment[] = [];
        try {
            let skip = 0;
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const outcome = await this.fakeReviewService.listComments(
                    null,
                    skip,
                    MANAGE_FETCH_CHUNK,
                );
                if (outcome.result !== 'Ok') {
                    this.manageError.set('Failed to load comments.');
                    return;
                }
                collected.push(...outcome.comments);
                if (outcome.comments.length < MANAGE_FETCH_CHUNK) break;
                skip += MANAGE_FETCH_CHUNK;
            }
            this.allComments.set(collected);
            // Drop any selected tags that no longer exist in the pool.
            const available = new Set<string>();
            for (const c of collected) for (const t of c.Tags) available.add(t);
            this.selectedTags.update((tags) =>
                tags.filter((t) => available.has(t)),
            );
        } finally {
            this.manageLoading.set(false);
        }
    }

    protected openEdit(comment: FakeReviewComment): void {
        this.editComment.set(comment);
        this.editForm.set({
            Text: comment.Text,
            TagsRaw: comment.Tags.join(', '),
            MinRating: comment.MinRating,
            MaxRating: comment.MaxRating,
        });
        this.editError.set(null);
        this.editDialogVisible.set(true);
    }

    protected closeEdit(): void {
        this.editDialogVisible.set(false);
        this.editComment.set(null);
        this.editForm.set(null);
        this.editError.set(null);
    }

    protected updateEditText(value: string): void {
        this.editForm.update((f) => (f ? { ...f, Text: value } : f));
    }

    protected updateEditTagsRaw(value: string): void {
        this.editForm.update((f) => (f ? { ...f, TagsRaw: value } : f));
    }

    protected updateEditMinRating(value: number): void {
        this.editForm.update((f) => (f ? { ...f, MinRating: value } : f));
    }

    protected updateEditMaxRating(value: number): void {
        this.editForm.update((f) => (f ? { ...f, MaxRating: value } : f));
    }

    protected async saveEdit(): Promise<void> {
        const orig = this.editComment();
        const form = this.editForm();
        if (!orig || !form) return;

        const trimmedText = form.Text.trim();
        if (trimmedText.length === 0) {
            this.editError.set("Text can't be empty.");
            return;
        }

        const tags = this.parseTags(form.TagsRaw);
        if (tags.length === 0) {
            this.editError.set('At least one tag is required.');
            return;
        }

        if (
            !Number.isFinite(form.MinRating) ||
            !Number.isFinite(form.MaxRating)
        ) {
            this.editError.set('Rating must be a number.');
            return;
        }
        if (form.MinRating < RATING_MIN || form.MinRating > RATING_MAX) {
            this.editError.set(
                `MinRating must be in [${RATING_MIN}..${RATING_MAX}].`,
            );
            return;
        }
        if (form.MaxRating < RATING_MIN || form.MaxRating > RATING_MAX) {
            this.editError.set(
                `MaxRating must be in [${RATING_MIN}..${RATING_MAX}].`,
            );
            return;
        }
        if (form.MinRating > form.MaxRating) {
            this.editError.set('MinRating must be ≤ MaxRating.');
            return;
        }

        const patch = this.computeCommentPatch(orig, {
            Text: trimmedText,
            Tags: tags,
            MinRating: form.MinRating,
            MaxRating: form.MaxRating,
        });

        if (
            patch.Text === null &&
            patch.Tags === null &&
            patch.MinRating === null &&
            patch.MaxRating === null
        ) {
            this.editError.set('No changes.');
            return;
        }

        this.editSaving.set(true);
        this.editError.set(null);
        try {
            const outcome = await this.fakeReviewService.updateComment(
                orig.Id,
                patch,
            );
            if (outcome.result === 'Ok') {
                this.applyCommentUpdate(outcome.comment);
                this.messageService.add({
                    severity: 'success',
                    summary: 'Comment updated',
                });
                this.closeEdit();
            } else if (outcome.result === 'NotFound') {
                this.editError.set(
                    'Comment not found — it may have been removed.',
                );
            } else if (outcome.result === 'InvalidRatingRange') {
                this.editError.set('Invalid rating range.');
            } else {
                this.editError.set('Failed to update comment.');
            }
        } finally {
            this.editSaving.set(false);
        }
    }

    private computeCommentPatch(
        orig: FakeReviewComment,
        next: {
            Text: string;
            Tags: string[];
            MinRating: number;
            MaxRating: number;
        },
    ): UpdateCommentPatch {
        return {
            Text: next.Text !== orig.Text ? next.Text : null,
            Tags: this.tagsEqual(next.Tags, orig.Tags) ? null : next.Tags,
            MinRating:
                next.MinRating !== orig.MinRating ? next.MinRating : null,
            MaxRating:
                next.MaxRating !== orig.MaxRating ? next.MaxRating : null,
        };
    }

    private tagsEqual(a: string[], b: string[]): boolean {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
        return true;
    }

    protected requestDelete(comment: FakeReviewComment): void {
        this.deleteConfirmComment.set(comment);
        this.deleteConfirmVisible.set(true);
    }

    protected cancelDelete(): void {
        this.deleteConfirmVisible.set(false);
        this.deleteConfirmComment.set(null);
    }

    protected async confirmDelete(): Promise<void> {
        const comment = this.deleteConfirmComment();
        if (!comment) return;
        this.deleting.set(true);
        try {
            const outcome = await this.fakeReviewService.deleteComment(
                comment.Id,
            );
            if (outcome.result === 'Ok') {
                this.removeCommentLocal(comment.Id);
                this.messageService.add({
                    severity: 'success',
                    summary: outcome.deleted
                        ? 'Comment deleted'
                        : 'Comment already gone',
                });
            }
        } finally {
            this.deleting.set(false);
            this.deleteConfirmVisible.set(false);
            this.deleteConfirmComment.set(null);
        }
    }

    private applyCommentUpdate(updated: FakeReviewComment): void {
        this.allComments.update((list) =>
            list.map((c) => (c.Id === updated.Id ? updated : c)),
        );
    }

    private removeCommentLocal(id: string): void {
        this.allComments.update((list) => list.filter((c) => c.Id !== id));
    }
}
